import { useState } from "react";
import { Loader2, CreditCard, Landmark, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DEPOSITOR = [["full_name", "Full name *"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["id_type", "ID type"], ["id_number", "ID number"]];
const CARD = [["cardholder_name", "Cardholder name *"], ["card_number", "Card number *"], ["expiry_month", "Exp. month (MM) *"], ["expiry_year", "Exp. year (YYYY) *"], ["cvv", "CVV *"]];
const BANK = [["account_holder", "Account holder *"], ["bank_name", "Bank name *"], ["account_number", "Account number / IBAN *"], ["routing_code", "Routing / sort code"], ["swift_bic", "SWIFT / BIC"], ["country", "Country"]];

const Field = ({ group, name, text, value, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{text}</Label>
    <Input data-testid={`pe-${group}-${name}`} value={value || ""} onChange={(e) => onChange(name, e.target.value)}
      type={name === "cvv" ? "password" : "text"} autoComplete="off" required={text.endsWith("*")} className="bg-muted/40 border-border" />
  </div>
);

const Section = ({ title, group, fields, vals, set }) => (
  <div className="space-y-3">
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map(([n, t]) => <Field key={n} group={group} name={n} text={t} value={vals[n]} onChange={set} />)}
    </div>
  </div>
);

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v != null));

export const EngineForm = ({ onDone }) => {
  const [kind, setKind] = useState("deposit");
  const [method, setMethod] = useState("card");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [dep, setDep] = useState({});
  const [card, setCard] = useState({});
  const [bank, setBank] = useState({});
  const [busy, setBusy] = useState(false);
  const upd = (setter) => (k, v) => setter((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { method, amount: parseFloat(amount), currency: "USD", description, depositor: clean(dep) };
    if (method === "card") body.card = { ...clean(card), expiry_month: parseInt(card.expiry_month, 10), expiry_year: parseInt(card.expiry_year, 10) };
    else body.bank = clean(bank);
    try {
      const { data } = await api.post(`/processor-engine/${kind}`, body);
      toast.success(`${kind === "deposit" ? "Deposit" : "Withdrawal"} recorded · ${data.instrument}`);
      setAmount(""); setDescription(""); setCard({}); setBank({}); setDep({});
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const pill = (active) => `flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-indigo-500 text-white" : "bg-muted/50 text-muted-foreground hover:text-foreground"}`;

  return (
    <form onSubmit={submit} data-testid="pe-form" className="rounded-xl border border-border bg-card p-5 space-y-5">
      <div className="flex gap-2">
        <button type="button" data-testid="pe-kind-deposit" onClick={() => setKind("deposit")} className={pill(kind === "deposit")}><ArrowDownLeft className="h-4 w-4" />Deposit (inflow)</button>
        <button type="button" data-testid="pe-kind-withdrawal" onClick={() => setKind("withdrawal")} className={pill(kind === "withdrawal")}><ArrowUpRight className="h-4 w-4" />Withdrawal (outflow)</button>
      </div>
      <div className="flex gap-2">
        <button type="button" data-testid="pe-method-card" onClick={() => setMethod("card")} className={pill(method === "card")}><CreditCard className="h-4 w-4" />Card</button>
        <button type="button" data-testid="pe-method-bank" onClick={() => setMethod("bank")} className={pill(method === "bank")}><Landmark className="h-4 w-4" />Bank account</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Amount (USD) *</Label>
          <Input data-testid="pe-amount" type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-muted/40 border-border font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Description</Label>
          <Input data-testid="pe-description" value={description} onChange={(e) => setDescription(e.target.value)} className="bg-muted/40 border-border" />
        </div>
      </div>
      <Section title={kind === "deposit" ? "Depositor information" : "Beneficiary information"} group="depositor" fields={DEPOSITOR} vals={dep} set={upd(setDep)} />
      {method === "card"
        ? <Section title="Card details (CVV is verified, never stored)" group="card" fields={CARD} vals={card} set={upd(setCard)} />
        : <Section title="Bank account details" group="bank" fields={BANK} vals={bank} set={upd(setBank)} />}
      <Button type="submit" disabled={busy} data-testid="pe-submit" className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Record ${kind}`}
      </Button>
    </form>
  );
};
